import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LeftMenu from '../components/LeftMenu';
import withAuth from '../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Swal from 'sweetalert2';
import AddSupplierModal from '../components/AddSupplierModal';
import { 
  BASE_URL,
  GET_SUPPLIER_MASTER,
  GET_MANUFACTURER_MASTER
} from '../utlilities/config';
import axios from 'axios';

// Icons
import { 
  FiSearch, FiPlus, FiTrash2, FiRefreshCw, 
  FiEdit,
  FiList, FiFileText
} from "react-icons/fi";
import { BsTruck } from "react-icons/bs";
import { FaBuilding } from "react-icons/fa";

// No Data component
const NoData = ({ type }) => (
  <div className="flex flex-col items-center justify-center h-64 py-10">
    <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-black-400">
      <FiFileText size={32} />
    </div>
    <h3 className="text-lg font-medium text-black-700">No {type} found</h3>
    <p className="text-sm text-black mt-1 mb-6">Try adjusting your search or add a new {type}</p>
  </div>
);

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading master data...</p>
    </div>
  </div>
);

const SupplierMaster = () => {
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('Supplier'); // 'Supplier' or 'Company'
  const [suppliers, setSuppliers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoverRow, setHoverRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const searchDebounceRef = useRef(null);

  const token = useMemo(() => localStorage.getItem('accessToken'), []);

  const defaultHeaders = useMemo(() => {
    const headers = {
      'XApiKey': process.env.REACT_APP_API_KEY,
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [token]);

  // Initial load - fetch first 2000 records
  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [supplierResponse, manufacturerResponse] = await Promise.all([
        axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, { 
          headers: {
            ...defaultHeaders,
            'FilterText': '',
          }
        }),
        axios.get(`${BASE_URL}${GET_MANUFACTURER_MASTER}`, { 
          headers: {
            ...defaultHeaders,
            'FilterText': '',
          }
        }),
      ]);

      if (supplierResponse.status === 200) {
        const supplierData = supplierResponse.data?.data || [];
        // Limit to first 2000 records
        setSuppliers(supplierData.slice(0, 2000));
      }

      if (manufacturerResponse.status === 200) {
        const manufacturerData = manufacturerResponse.data?.data || [];
        // Limit to first 2000 records
        setManufacturers(manufacturerData.slice(0, 2000));
      }
    } catch (error) {
      console.error('Failed to fetch supplier/manufacturer data:', error);
      toast.error('Could not load supplier/company data', {
        autoClose: 1500,
        icon: <FiFileText className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // Fetch suppliers from API based on search text
  const getSuppliersBySearch = useCallback(async (filterText) => {
    if (!filterText || filterText.length < 2) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, {
        headers: {
          ...defaultHeaders,
          'FilterText': filterText,
        }
      });

      if (response.status === 200) {
        const supplierData = response.data?.data || [];
        setSuppliers(supplierData);
      }
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
      toast.error('Failed to fetch suppliers.', {
        autoClose: 1000,
        icon: <FiFileText className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders]);

  // Fetch manufacturers from API based on search text
  const getManufacturersBySearch = useCallback(async (filterText) => {
    if (!filterText || filterText.length < 2) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_MANUFACTURER_MASTER}`, {
        headers: {
          ...defaultHeaders,
          'FilterText': filterText,
        }
      });

      if (response.status === 200) {
        const manufacturerData = response.data?.data || [];
        setManufacturers(manufacturerData);
      }
    } catch (error) {
      console.error('Failed to fetch manufacturers:', error);
      toast.error('Failed to fetch manufacturers.', {
        autoClose: 1000,
        icon: <FiFileText className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders]);

  const refreshSuppliers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_SUPPLIER_MASTER}`, { 
        headers: {
          ...defaultHeaders,
          'FilterText': '',
        }
      });
      if (response.status === 200) {
        const supplierData = response.data?.data || [];
        // Limit to first 2000 records
        setSuppliers(supplierData.slice(0, 2000));
      }
    } catch (error) {
      console.error('Failed to refresh supplier data:', error);
      toast.error('Unable to refresh suppliers', {
        autoClose: 1500,
        icon: <FiFileText className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders]);

  const refreshManufacturers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_MANUFACTURER_MASTER}`, { 
        headers: {
          ...defaultHeaders,
          'FilterText': '',
        }
      });
      if (response.status === 200) {
        const manufacturerData = response.data?.data || [];
        // Limit to first 2000 records
        setManufacturers(manufacturerData.slice(0, 2000));
      }
    } catch (error) {
      console.error('Failed to refresh manufacturer data:', error);
      toast.error('Unable to refresh manufacturers', {
        autoClose: 1500,
        icon: <FiFileText className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  }, [defaultHeaders]);

  const toggleMenu = () => {
    setIsMenuExpanded(!isMenuExpanded);
  };

  const handleClick = () => {
    setSelectedItem(null);
    setShowModal(true);
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setShowModal(true);
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    setSearchQuery(searchValue);
    
    // Clear previous debounce timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    
    // Call API when user types 2+ characters with debounce
    if (searchValue && searchValue.length >= 2) {
      searchDebounceRef.current = setTimeout(() => {
        if (filter === 'Supplier') {
          getSuppliersBySearch(searchValue);
        } else {
          getManufacturersBySearch(searchValue);
        }
      }, 300); // 300ms debounce
    } else if (searchValue.length === 0) {
      // Reload initial 2000 records when search is cleared
      fetchInitialData();
    }
  };

  const handleDeleteClick = (item) => {
    Swal.fire({
      title: `Delete ${filter}?`,
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'rounded-lg text-sm font-medium px-5 py-2.5',
        cancelButton: 'rounded-lg text-sm font-medium px-5 py-2.5 mr-2',
        popup: 'rounded-xl shadow-xl'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // TODO: Integrate delete API once backend contract is finalised
        toast.info(`${filter} deletion will be available soon.`, {
          autoClose: 1500
        });
      }
    });
  };

  const handleRefresh = () => {
    if (filter === 'Supplier') {
      refreshSuppliers();
    } else {
      refreshManufacturers();
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  // Products are already filtered from API, but we can do additional client-side filtering/sorting if needed
  const filteredSuppliers = useMemo(() => {
    const collection = suppliers || [];
    // API already filters, but we can do additional client-side refinement if needed
    const sorted = [...collection].sort((a, b) => {
      const idA = Number(a?.SupplierID ?? a?.supplierID ?? a?.id ?? 0);
      const idB = Number(b?.SupplierID ?? b?.supplierID ?? b?.id ?? 0);
      return idA - idB;
    });

    return sorted;
  }, [suppliers]);

  const filteredManufacturers = useMemo(() => {
    const collection = manufacturers || [];
    // API already filters, but we can do additional client-side refinement if needed
    const sorted = [...collection].sort((a, b) => {
      const idA = Number(a?.ManufactureID ?? a?.manufacturerID ?? a?.Id ?? a?.id ?? 0);
      const idB = Number(b?.ManufactureID ?? b?.manufacturerID ?? b?.Id ?? b?.id ?? 0);
      return idA - idB;
    });

    return sorted;
  }, [manufacturers]);

  const currentData = filter === 'Supplier' ? filteredSuppliers : filteredManufacturers;

  return (
    <div className="flex h-full bg-background">
      {loading && <Spinner />}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {/* <LeftMenu isExpanded={isMenuExpanded} toggleMenu={toggleMenu} /> */}
      
      <div className="p-6 flex-1 flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-md p-3 mb-3">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-bold text-black-800 flex items-center">
              Supplier / Company Master
            </h1>
            
            <button 
              className="px-4 py-2.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center"
              onClick={handleClick}
            >
              <FiPlus className="mr-2" /> Add New {filter}
            </button>
          </div>
          
        </div>
        
        {/* Filters and Search */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <div className="flex flex-wrap justify-between items-center">
            {/* Tabs */}
            <div className="flex space-x-1 mb-4 md:mb-0">
              <button
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filter === 'Supplier' 
                    ? 'bg-indigo-100 text-indigo-700' 
                    : 'text-black hover:bg-gray-100'
                }`}
                onClick={() => {
                  setFilter('Supplier');
                  setSearchQuery("");
                  // Reload initial data when switching tabs
                  fetchInitialData();
                }}
              >
                <span className="flex items-center">
                  <BsTruck className="mr-2" />
                  Supplier
                </span>
              </button>
              
              <button
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  filter === 'Company' 
                    ? 'bg-indigo-100 text-indigo-700' 
                    : 'text-black hover:bg-gray-100'
                }`}
                onClick={() => {
                  setFilter('Company');
                  setSearchQuery("");
                  // Reload initial data when switching tabs
                  fetchInitialData();
                }}
              >
                <span className="flex items-center">
                  <FaBuilding className="mr-2" />
                  Company
                </span>
              </button>
            </div>
            
            {/* Search */}
            <div className="relative w-full md:w-72">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-black-400">
                <FiSearch size={16} />
              </div>
              <input
                type="text"
                placeholder={`Search by ${filter === 'Supplier' ? 'code, name, place, city, state, mobile, GST or email' : 'code, name, address, place, contact person or short name'}`}
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
        
        {/* Results Count */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-black">
            {currentData.length} 
            {currentData.length === 1 ? ` ${filter.toLowerCase()}` : ` ${filter.toLowerCase()}s`} found
            {searchQuery && ` for "${searchQuery}"`}
          </div>
          
          <button 
            onClick={handleRefresh}
            className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
          >
            <FiRefreshCw className="mr-1" size={14} /> Refresh
          </button>
        </div>
        
        {/* Table */}
        <div className="bg-white rounded-xl shadow-md flex-1 overflow-hidden">
          {currentData.length > 0 ? (
            <div className="overflow-x-auto h-full">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {filter === 'Supplier' ? (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Supplier Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Place</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">City</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">State</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Pincode</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Mobile</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Phone1</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Phone2</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">GST Number</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black whitespace-nowrap uppercase tracking-wider border-b">Contact Person</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black whitespace-nowrap uppercase tracking-wider border-b">Contact Number</th>
                      </>
                    ) : (
                      <>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Short Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Address</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Place</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black whitespace-nowrap uppercase tracking-wider border-b">Contact Person</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Number</th>
                      </>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">Actions</th>
                  </tr>
                </thead>
                
                <tbody className="divide-y divide-gray-100">
                  {filter === 'Supplier' ? (
                    filteredSuppliers.map((supplier, index) => (
                      <tr 
                        key={supplier.SupplierID || supplier.id || index}
                        className={`${
                          hoverRow === index ? 'bg-indigo-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } transition-colors hover:bg-indigo-50`}
                        onMouseEnter={() => setHoverRow(index)}
                        onMouseLeave={() => setHoverRow(null)}
                      >
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.SupplierName}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.Address || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.Place || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.City || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.State || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.Pincode || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.MobileNo || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.Phone1 || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.Phone2 || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.Email || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.GstNo || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.ContactPersonName || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{supplier.ContactPersonNumber || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">
                          <div className="flex space-x-3">
                            <button
                              className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                              onClick={() => handleEditClick(supplier)}
                              title="Edit supplier"
                            >
                              <FiEdit size={16} />
                            </button>
                            <button
                              className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                              onClick={() => handleDeleteClick(supplier)}
                              title="Delete supplier"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    filteredManufacturers.map((manufacturer, index) => (
                      <tr 
                        key={manufacturer.Id || manufacturer.ID || index}
                        className={`${
                          hoverRow === index ? 'bg-indigo-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } transition-colors hover:bg-indigo-50`}
                        onMouseEnter={() => setHoverRow(index)}
                        onMouseLeave={() => setHoverRow(null)}
                      >
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{manufacturer.Name}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{manufacturer.ShortName || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{manufacturer.Address || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{manufacturer.Place || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{manufacturer.ContactPerson || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">{manufacturer.Number || '-'}</td>
                        <td className="px-6 py-2 text-sm text-black whitespace-nowrap">
                          <div className="flex space-x-3">
                            <button
                              className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                              onClick={() => handleEditClick(manufacturer)}
                              title="Edit company"
                            >
                              <FiEdit size={16} />
                            </button>
                            <button
                              className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                              onClick={() => handleDeleteClick(manufacturer)}
                              title="Delete company"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <NoData type={filter.toLowerCase()} />
          )}
        </div>
      </div>
      
      {/* Modal */}
      {showModal && (
        <AddSupplierModal 
          setShowModal={handleModalClose} 
          onSave={filter === 'Supplier' ? refreshSuppliers : refreshManufacturers}
          selectedItem={selectedItem}
          setSelectedItem={setSelectedItem}
          mode={filter}
        />
      )}
    </div>
  );
};

export default withAuth(SupplierMaster);
